import React, { useState } from 'react';
import { IAuditoriaCambialResultado, IAuditoriaCambialItem } from '../interfaces/IAuditoriaCambial';

interface Props {
  resultado: IAuditoriaCambialResultado;
}

const formatCurrency = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatPct = (v: number) => `${v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
const formatRate = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 });

// COR-224: null = não calculado (sem base patrimonial mensurável ou API BCB indisponível) — nunca
// renderizar como "0,0000"/"0,00%", que pareceria um cálculo real com resultado zero.
const formatRateOuNA = (v: number | null) => (v === null ? 'Não calculado' : formatRate(v));
const formatPctOuNA = (v: number | null) => (v === null ? 'N/A' : formatPct(v));
const formatCurrencyOuNA = (v: number | null) => (v === null ? 'Não calculado' : formatCurrency(v));

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cor =
    status === 'ALERTA' ? '#f59e0b' :
    status === 'AVISO'  ? '#3b82f6' :
                          '#22c55e';
  return (
    <span
      className='badge'
      style={{ backgroundColor: cor, color: status === 'CONCILIADO' ? 'white' : 'black' }}
    >
      {status}
    </span>
  );
};

const ItemRow: React.FC<{ item: IAuditoriaCambialItem }> = ({ item }) => {
  const [expandido, setExpandido] = useState(false);

  return (
    <>
      <tr
        onClick={() => setExpandido(!expandido)}
        style={{ cursor: 'pointer', backgroundColor: item.status === 'ALERTA' ? '#fef9c3' : 'white' }}
      >
        <td>{item.empresaNome}</td>
        <td><code>{item.cnpj}</code></td>
        <td className='text-end'>{formatCurrency(item.saldoFinal)}</td>
        <td className='text-end'>{formatCurrency(item.varContabil)}</td>
        <td className='text-end'>{formatRateOuNA(item.ptaxFimUsd)}</td>
        <td className='text-end'>{formatRateOuNA(item.ptaxFimEur)}</td>
        <td className='text-end'>
          <span style={{ color: item.desvioUsdPct !== null && item.desvioUsdPct > 5 ? '#dc2626' : item.desvioUsdPct === null ? '#6b7280' : '#16a34a' }}>
            {formatPctOuNA(item.desvioUsdPct)}
          </span>
        </td>
        <td className='text-end'>
          <span style={{ color: item.desvioEurPct !== null && item.desvioEurPct > 5 ? '#dc2626' : item.desvioEurPct === null ? '#6b7280' : '#16a34a' }}>
            {formatPctOuNA(item.desvioEurPct)}
          </span>
        </td>
        <td className='text-end' style={{ color: item.exposicaoFiscalTotal > 0 ? '#dc2626' : '#16a34a' }}>
          {formatCurrency(item.exposicaoFiscalTotal)}
        </td>
        <td><StatusBadge status={item.status} /></td>
      </tr>

      {expandido && (
        <tr>
          <td colSpan={10} style={{ backgroundColor: '#f8fafc', padding: '1rem' }}>

            {/* Mensagem diagnóstico */}
            <div
              className='alert mb-3 py-2'
              style={{
                backgroundColor: item.status === 'ALERTA' ? '#fef9c3' : item.status === 'AVISO' ? '#eff6ff' : '#f0fdf4',
                border: `1px solid ${item.status === 'ALERTA' ? '#fbbf24' : item.status === 'AVISO' ? '#93c5fd' : '#86efac'}`,
              }}
            >
              <small>{item.mensagemDiagnostico}</small>
            </div>

            {/* Memória de cálculo */}
            <div className='row g-3 mb-3'>
              <div className='col-md-6'>
                <h6 className='text-muted mb-2'>Hipótese USD (BCB Série 1)</h6>
                <table className='table table-sm table-bordered mb-0'>
                  <tbody>
                    <tr><td>PTAX Início</td><td className='text-end'>{formatRateOuNA(item.ptaxInicioUsd)}</td></tr>
                    <tr><td>PTAX Fim</td><td className='text-end'>{formatRateOuNA(item.ptaxFimUsd)}</td></tr>
                    <tr><td>Var. PTAX Esperada</td><td className='text-end'>{formatCurrencyOuNA(item.varPtaxUsd)}</td></tr>
                    <tr><td>Var. Contábil Registrada</td><td className='text-end'>{formatCurrency(item.varContabil)}</td></tr>
                    <tr>
                      <td><strong>Desvio USD</strong></td>
                      <td className='text-end'>
                        <strong style={{ color: item.desvioUsdPct !== null && item.desvioUsdPct > 5 ? '#dc2626' : item.desvioUsdPct === null ? '#6b7280' : '#16a34a' }}>
                          {formatPctOuNA(item.desvioUsdPct)}
                        </strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className='col-md-6'>
                <h6 className='text-muted mb-2'>Hipótese EUR (BCB Série 21619)</h6>
                <table className='table table-sm table-bordered mb-0'>
                  <tbody>
                    <tr><td>PTAX Início</td><td className='text-end'>{formatRateOuNA(item.ptaxInicioEur)}</td></tr>
                    <tr><td>PTAX Fim</td><td className='text-end'>{formatRateOuNA(item.ptaxFimEur)}</td></tr>
                    <tr><td>Var. PTAX Esperada</td><td className='text-end'>{formatCurrencyOuNA(item.varPtaxEur)}</td></tr>
                    <tr><td>Var. Contábil Registrada</td><td className='text-end'>{formatCurrency(item.varContabil)}</td></tr>
                    <tr>
                      <td><strong>Desvio EUR</strong></td>
                      <td className='text-end'>
                        <strong style={{ color: item.desvioEurPct !== null && item.desvioEurPct > 5 ? '#dc2626' : item.desvioEurPct === null ? '#6b7280' : '#16a34a' }}>
                          {formatPctOuNA(item.desvioEurPct)}
                        </strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {!item.possuiBaseExposicaoMensuravel && (
              <div className='alert alert-secondary py-2 mb-3'>
                <small>
                  <strong>Indícios cambiais identificados, porém sem saldo patrimonial mensurável para
                  cálculo de exposição/PTAX.</strong> Os campos acima aparecem como "Não calculado"/"N/A"
                  porque a auditoria não chegou a ter base de exposição patrimonial para comparar com a
                  PTAX — não representam um cálculo com resultado zero.
                </small>
              </div>
            )}

            {/* Passivo fiscal */}
            {item.status === 'ALERTA' && (
              <div className='mb-3'>
                <h6 className='text-muted mb-2'>Passivo Fiscal Estimado</h6>
                <table className='table table-sm table-bordered mb-0' style={{ maxWidth: 400 }}>
                  <tbody>
                    <tr><td>Passivo Principal (Tf = div. × 34%)</td><td className='text-end text-danger'><strong>{formatCurrency(item.passivoFiscal)}</strong></td></tr>
                    <tr><td>Multa Qualificada (MQ = Tf × 75%)</td><td className='text-end text-danger'>{formatCurrency(item.multaQualificada)}</td></tr>
                    <tr><td>Multa Acessória (MA = Saldo × 3%)</td><td className='text-end text-danger'>{formatCurrency(item.multaAcessoria)}</td></tr>
                    <tr style={{ backgroundColor: '#fee2e2' }}>
                      <td><strong>Exposição Total (Et = Tf + MQ + MA)</strong></td>
                      <td className='text-end text-danger'><strong>{formatCurrency(item.exposicaoFiscalTotal)}</strong></td>
                    </tr>
                  </tbody>
                </table>
                <small className='text-muted mt-1 d-block'>
                  * IRPJ/CSLL tributam apenas no fato gerador (baixa/liquidação). A variação cambial
                  de competência é ajustada via Livro F (e-LACS/LALUR).
                </small>
              </div>
            )}

            {/* Auditoria de estorno */}
            <div className='mb-3'>
              <h6 className='text-muted mb-2'>Auditoria de Estorno e Lançamento</h6>
              <div className='d-flex gap-3'>
                <span className={`badge ${item.estornoMesAnteriorIdentificado ? 'bg-success' : 'bg-secondary'}`}>
                  {item.estornoMesAnteriorIdentificado ? '✓ Estorno Mês N-1 Identificado' : 'Estorno Mês N-1 Não Identificado'}
                </span>
                <span className={`badge ${item.novoLancamentoMesAtualRealizado ? 'bg-success' : 'bg-secondary'}`}>
                  {item.novoLancamentoMesAtualRealizado ? '✓ Novo Lançamento Mês N Realizado' : 'Novo Lançamento Mês N Ausente'}
                </span>
              </div>
            </div>

            {/* Lançamentos patrimoniais / lançamentos-gatilho de indício cambial (COR-223) */}
            {item.lancamentosPatrimoniais.length > 0 && (
              <div className='mb-2'>
                <h6 className='text-muted mb-2'>
                  {item.possuiBaseExposicaoMensuravel
                    ? `Lançamentos Patrimoniais (${item.lancamentosPatrimoniais.length})`
                    : `Lançamentos com Indício Cambial Identificados (${item.lancamentosPatrimoniais.length})`}
                </h6>
                {!item.possuiBaseExposicaoMensuravel && (
                  <p className='text-muted' style={{ fontSize: '0.8rem' }}>
                    Lançamentos cuja conta e/ou histórico contêm termo cambial — motivo do AVISO exibido acima.
                    Sem exposição patrimonial mensurável (diferente de exposição cambial efetiva).
                  </p>
                )}
                <table className='table table-sm table-striped mb-0'>
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Nº Lançamento</th>
                      <th>Conta</th>
                      <th>Descrição da Conta</th>
                      <th>Nat.</th>
                      <th>Histórico</th>
                      <th className='text-end'>Valor</th>
                      {!item.possuiBaseExposicaoMensuravel && (
                        <>
                          <th>Termo Acionador</th>
                          <th>Classificação do Indício</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {item.lancamentosPatrimoniais.map((l) => (
                      <tr key={l.lancamentoId}>
                        <td>{new Date(l.dataLancamento).toLocaleDateString('pt-BR')}</td>
                        <td>{l.numeroLancamento || '—'}</td>
                        <td><code>{l.codigoContabil}</code></td>
                        <td>{l.descricaoConta || '—'}</td>
                        <td>{l.natureza}</td>
                        <td style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.historico}</td>
                        <td className='text-end'>{formatCurrency(l.valor)}</td>
                        {!item.possuiBaseExposicaoMensuravel && (
                          <>
                            <td><code>{l.termoAcionador || '—'}</code></td>
                            <td>
                              <span className='badge bg-info text-dark'>{l.classificacaoIndicio || '—'}</span>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Lançamentos de resultado cambial */}
            {item.lancamentosResultado.length > 0 && (
              <div>
                <h6 className='text-muted mb-2'>
                  Variação Cambial Registrada ({item.lancamentosResultado.length})
                </h6>
                <table className='table table-sm table-striped mb-0'>
                  <thead>
                    <tr>
                      <th>Conta</th>
                      <th>Data</th>
                      <th>Histórico</th>
                      <th className='text-end'>Valor</th>
                      <th>Nat.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {item.lancamentosResultado.map((l) => (
                      <tr key={l.lancamentoId}>
                        <td><code>{l.codigoContabil}</code></td>
                        <td>{new Date(l.dataLancamento).toLocaleDateString('pt-BR')}</td>
                        <td style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.historico}</td>
                        <td className='text-end'>{formatCurrency(l.valor)}</td>
                        <td>{l.natureza}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

const AuditoriaCambialDetalhe: React.FC<Props> = ({ resultado }) => {
  const temAlerta = resultado.totalAlertas > 0;
  const temAviso  = resultado.totalAvisos > 0;

  return (
    <div className='auditoria-card'>
      <h4>Gestão de Exposição Cambial e Eficiência de Hedge</h4>
      <p className='text-muted'>
        Compara a variação cambial contabilizada com a PTAX oficial do Banco Central (USD e EUR).
        Desvio {'>'} 5% em ambas as moedas → ALERTA DE RISCO (processo não travado). Contas ou
        históricos com indício cambial (câmbio, IOF câmbio, tarifa de contrato de câmbio, variação
        cambial, hedge, exterior ou moeda estrangeira) sem base patrimonial mensurável para o cálculo
        geram AVISO, não CONCILIADO — os lançamentos que motivaram o aviso ficam detalhados abaixo, e
        os campos de PTAX/desvio aparecem como "Não calculado"/"N/A", nunca como 0,00 calculado.
      </p>

      {/* Banner de alerta */}
      {temAlerta && (
        <div className='alert alert-warning mb-4'>
          <strong>ALERTA DE RISCO: Paridade Cambial Divergente</strong>
          <br />
          <small>{resultado.mensagemDirecionamento}</small>
        </div>
      )}
      {temAviso && !temAlerta && (
        <div className='alert alert-info mb-4'>
          <strong>AVISO</strong>
          <br />
          <small>{resultado.mensagemDirecionamento}</small>
        </div>
      )}

      {/* KPIs */}
      <div className='auditoria-kpis mb-4' style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className={`auditoria-kpi ${resultado.exposicaoFiscalTotalGeral > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Exposição Fiscal Total</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.exposicaoFiscalTotalGeral)}</div>
          <div className='auditoria-kpi-help'>Et = Tf + MQ + MA</div>
        </div>
        <div className={`auditoria-kpi ${resultado.passivoFiscalTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Passivo Fiscal (Tf 34%)</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.passivoFiscalTotal)}</div>
          <div className='auditoria-kpi-help'>divergência × (IRPJ 25% + CSLL 9%)</div>
        </div>
        <div className={`auditoria-kpi ${(resultado.multaQualificadaTotal + resultado.multaAcessoriaTotal) > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Total Multas (MQ+MA)</div>
          <div className='auditoria-kpi-value'>
            {formatCurrency(resultado.multaQualificadaTotal + resultado.multaAcessoriaTotal)}
          </div>
          <div className='auditoria-kpi-help'>MQ = Tf×75% + MA = Saldo×3%</div>
        </div>
        <div className='auditoria-kpi auditoria-kpi-disabled'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value' style={{ fontSize: '0.7rem', lineHeight: 1.4 }}>
            RIR/99 Art.375 · Lei 12.973/14 Art.4º · IN RFB 1.700/17
          </div>
        </div>
      </div>

      {/* Itens */}
      {resultado.itens.length === 0 ? (
        <div className='auditoria-empty-state'>
          <p>Nenhuma conta, saldo ou histórico com indício cambial identificado para o período selecionado.</p>
        </div>
      ) : (
        <div className='table-responsive'>
          <table className='table table-hover align-middle'>
            <thead className='table-light'>
              <tr>
                <th>Empresa</th>
                <th>CNPJ</th>
                <th className='text-end'>Saldo Exposição</th>
                <th className='text-end'>Var. Contábil</th>
                <th className='text-end'>PTAX Fim USD</th>
                <th className='text-end'>PTAX Fim EUR</th>
                <th className='text-end'>Desvio USD</th>
                <th className='text-end'>Desvio EUR</th>
                <th className='text-end'>Exposição Fiscal</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {resultado.itens.map((item) => (
                <ItemRow key={`${item.empresaId}-${item.cnpj}`} item={item} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Fundamentação legal */}
      {resultado.fundamentacaoLegal && (
        <details className='mt-4'>
          <summary className='text-muted' style={{ cursor: 'pointer', fontSize: '0.85rem' }}>
            Fundamentação Legal
          </summary>
          <div className='mt-2 p-3' style={{ backgroundColor: '#f8fafc', fontSize: '0.8rem', lineHeight: 1.6 }}>
            {resultado.fundamentacaoLegal}
          </div>
        </details>
      )}
    </div>
  );
};

export default AuditoriaCambialDetalhe;
